import { useMutation } from '@apollo/client';
import {
  Form,
  InfoTooltip,
  PillGroup,
  TextBox,
} from '@homex/se-react-components';
import {
  CarouselItemId,
  CustomerNotificationsOptions,
} from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  reportBookingComplete,
  setAcceleratedEntryPoint,
  setAppointmentId,
  setCurrentCarouselItem,
  setCurrentCarouselItemToPrevious,
  setEmail,
  setIsDispatchFeeAccepted,
  setLeadId,
  setNotificationPreference,
  setSelectedTimeSlot,
} from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { CheckBox } from 'components/CheckBox';
import { Phrase } from 'components/Phrase';
import { useStringContext } from 'components/StringContext';
import { ReactComponent as InputEmailSvg } from 'components/svg/InputEmail.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselBottomButtonType,
  CarouselReduxReducer,
  IState,
  NotificationPreference,
  RepairSummaryCarouselItem,
  ServiceType,
} from 'typings';
import {
  ICustomerInput,
  ISubmitBookingRequestMutation,
  ISubmitBookingRequestMutationVariables,
} from 'typings/GraphQL';
import { buildBookingOptions } from 'utils/buildBookingOptions';
import { EMAIL_REGEXP } from 'utils/emailRegexp';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import {
  CheckBoxContainer,
  Container,
  DispatchFeeDescription,
  EmailContainer,
  EmailTitle,
  NotificationPreferenceContainer,
  PillContainer,
} from './styles';

const submitBookingRequestMutation = loader(
  '../../queries/submitBookingRequest.graphql',
);

export const CAROUSEL_ITEM_ID = CarouselItemId.RepairSummary;

export const CarouselItemRepairSummaryBottom = () => {
  const dispatch = useThunkDispatch();

  const {
    accountTracking,
    addressId,
    analytics,
    currentItemId,
    customerLocationPhone,
    customerPhone,
    customForms,
    firstName,
    flow,
    email = '',
    erpKey,
    isDispatchFeeAccepted,
    isEmergency,
    issueStack,
    lastName,
    leadId,
    mobileNotificationOptIn,
    notificationPreference,
    preferredPartnerId,
    repairComments,
    selectedEnergySources,
    selectedEquipment,
    selectedSchedulePreference,
    selectedSchedulePreferenceTime,
    selectedTimeSlot,
    serviceCodeId,
    serviceType = ServiceType.REPAIR,
  } = useSelector((state: IState) => ({
    accountTracking: state.accountTracking,
    addressId: state.userInput.address.id,
    analytics: state.sourceTrackingAnalytics,
    currentItemId: state.serviceCarousel.currentItemId,
    customForms: state.userInput.customForms,
    customerLocationPhone: state.userInput.customerLocationPhone,
    customerPhone: state.userInput.customerPhone,
    email: state.userInput.email,
    erpKey: state.client.erpKey,
    firstName: state.userInput.firstName,
    flow: state.client.flow,
    history: state.serviceCarousel.history,
    isDispatchFeeAccepted: state.userInput.isDispatchFeeAccepted,
    isEmergency: state.userInput.isEmergency,
    isPreviousCustomer: state.userInput.isPreviousCustomer,
    issueStack: state.userInput.issueStack,
    lastName: state.userInput.lastName,
    leadId: state.userInput.leadId,
    mobileNotificationOptIn: state.userInput.mobileNotificationOptIn,
    // Make a copy of notificationPreference rather than passing the array itself. This is to avoid inadvertently
    // mutating state.userInput.notificationPreference when we mutate it in the render function
    notificationPreference: [...state.userInput.notificationPreference],
    preferredPartnerId: state.userInput.preferredPartner?.partnerId,
    repairComments: state.userInput.repairComments,
    selectedEnergySources: state.userInput.selectedEnergySources,
    selectedEquipment: state.userInput.selectedEquipment,
    selectedSchedulePreference: state.userInput.selectedSchedulePreference,
    selectedSchedulePreferenceTime:
      state.userInput.selectedSchedulePreferenceTime,
    selectedTimeSlot: state.userInput.selectedTimeSlot,
    serviceCodeId: state.userInput.serviceCodeId,
    serviceType: state.userInput.serviceType,
  }));

  const flowCarouselItem = tryGetFlowCarouselItem<RepairSummaryCarouselItem>(
    flow,
    CAROUSEL_ITEM_ID,
  );

  const flowId = flow?.id;

  const strings = useStringContext();

  const {
    buttonType,
    customerNotificationPreferencesEnabled = true,
    dispatchFeeTooltipEnabled = true,
    enterpriseEnabled,
    enterpriseVersion,
    optionalCustomerEmailFieldEnabled = true,
    requiredCustomerEmailFieldEnabled = false,
    schedulePreferenceEnabled,
  } = useTheme();

  const bottomButtonFormat = flow?.buttonType ?? buttonType;

  let flowNotificationOption: CustomerNotificationsOptions | undefined =
    undefined;

  // use flow version of settings
  if (flowCarouselItem) {
    flowNotificationOption =
      flowCarouselItem.options.customerNotificationsOptions;
  }

  const [submitBookingRequest, { error, loading: isLoading }] = useMutation<
    ISubmitBookingRequestMutation,
    ISubmitBookingRequestMutationVariables
  >(submitBookingRequestMutation, {
    onCompleted: (result: ISubmitBookingRequestMutation) => {
      const {
        appointment = null,
        errorCode = null,
        lead = null,
      } = result.submitBookingRequest || {};

      if (errorCode && selectedTimeSlot) {
        dispatch(
          setSelectedTimeSlot({ ...selectedTimeSlot, isUnavailable: true }),
        );
      } else if (appointment || lead) {
        if (appointment) {
          dispatch(setAppointmentId(appointment.id));
        }

        if (lead) {
          dispatch(setLeadId(lead.id));
        }

        let destinationId;

        if (flowCarouselItem) {
          destinationId = flowCarouselItem.options.destinations.next;
        } else {
          destinationId = CarouselItemId.ConfirmFinishRepair;
        }

        dispatch(reportBookingComplete());
        dispatch(setAcceleratedEntryPoint(null));
        dispatch(
          setCurrentCarouselItem(CarouselReduxReducer.SERVICE, destinationId),
        );
      }
    },
  });

  const handleBackToCalendar = () => {
    let destinationId;

    if (flowCarouselItem) {
      destinationId = flowCarouselItem.options.destinations.notAvailable;
    } else {
      destinationId = schedulePreferenceEnabled
        ? CarouselItemId.SchedulePreference
        : CarouselItemId.ScheduleCalendarRepair;
    }

    dispatch(
      setCurrentCarouselItemToPrevious(
        CarouselReduxReducer.SERVICE,
        destinationId,
      ),
    );
  };

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setIsDispatchFeeAccepted(event.currentTarget.checked));
  };

  const handleEmailChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setEmail(event.currentTarget.value));
  };

  const handleNotificationPreferenceChange = (
    _event: React.FormEvent<HTMLInputElement>,
    value?: string | Array<string>,
  ) => {
    if (Array.isArray(value)) {
      dispatch(
        setNotificationPreference(value as Array<NotificationPreference>),
      );
    }
  };

  const handleContinueClick = () => {
    if (!selectedTimeSlot) {
      return;
    }

    if (selectedTimeSlot.isUnavailable) {
      handleBackToCalendar();

      return;
    }

    const phoneId = customerPhone.id ?? '';
    const phoneIdForNotification =
      [customerPhone, customerLocationPhone].find(({ isMobile }) => !!isMobile)
        ?.id ?? phoneId;

    const isEmailIncluded =
      (!!email &&
        ((flowNotificationOption &&
          flowNotificationOption !==
            CustomerNotificationsOptions.NoNotificationOption) ||
          /** replace the block below when theme gets deprecated */
          (customerNotificationPreferencesEnabled === false &&
            optionalCustomerEmailFieldEnabled === true) ||
          requiredCustomerEmailFieldEnabled === true)) ||
      notificationPreference.includes(NotificationPreference.EMAIL);

    const customer: ICustomerInput = {
      addressId: addressId || '',
      email: isEmailIncluded ? email : undefined,
      firstName: firstName || '',
      lastName: lastName || '',
      notificationPreference:
        (flowNotificationOption &&
          flowNotificationOption ===
            CustomerNotificationsOptions.TextOrEmail) ||
        customerNotificationPreferencesEnabled
          ? notificationPreference
          : [],
      phoneId,
      phoneIdForNotification,
    };

    const options = buildBookingOptions({
      accountTracking,
      analytics,
      customForms,
      enterpriseEnabled,
      enterpriseVersion,
      erpKey,
      flowId,
      isEmergency,
      issueStack,
      leadId,
      mobileNotificationOptIn,
      preferredPartnerId,
      repairComments,
      schedulePreferenceEnabled,
      selectedEnergySources,
      selectedEquipment,
      selectedSchedulePreference,
      selectedSchedulePreferenceTime,
      serviceCodeId,
      strings,
    });

    submitBookingRequest({
      variables: {
        customer,
        options,
        serviceType,
        startTime: selectedTimeSlot.startTime,
      },
    });
  };

  const mobilePhone = [customerPhone, customerLocationPhone].find(
    ({ isMobile }) => !!isMobile,
  );
  let options = [
    {
      key: NotificationPreference.EMAIL,
      label: Phrase({ id: 'REPAIR_SUMMARY.EMAIL.BUTTON' }),
      value: NotificationPreference.EMAIL,
    },
    {
      isDisabled: !mobilePhone,
      key: NotificationPreference.SMS,
      label: Phrase({ id: 'REPAIR_SUMMARY.TEXT.BUTTON' }),
      value: NotificationPreference.SMS,
    },
  ];

  // If email is required. Lets remove the option to select/de-select email via pill options
  if (
    flowNotificationOption === CustomerNotificationsOptions.RequiredEmail ||
    requiredCustomerEmailFieldEnabled
  ) {
    options = options.filter((o) => o.key !== NotificationPreference.EMAIL);
  }

  // If there is no mobile phone, but notificationPreference has SMS in it, we need to remove SMS
  const smsIndex = notificationPreference.indexOf(NotificationPreference.SMS);

  if (!mobilePhone && smsIndex >= 0) {
    notificationPreference.splice(smsIndex, 1);
  }

  // If requiredCustomerEmailFieldEnabled is true Email is a auto-included notification preference
  // This only applies if customerNotificationPreferencesEnabled is true
  if (
    ((flowNotificationOption &&
      (flowNotificationOption === CustomerNotificationsOptions.TextOrEmail ||
        flowNotificationOption ===
          CustomerNotificationsOptions.RequiredEmail)) ||
      (requiredCustomerEmailFieldEnabled &&
        customerNotificationPreferencesEnabled)) &&
    !notificationPreference.find((np) => np === NotificationPreference.EMAIL)
  ) {
    notificationPreference.push(NotificationPreference.EMAIL);
  }

  const isEmailChecked = notificationPreference.includes(
    NotificationPreference.EMAIL,
  );

  let isEmailValid = true;

  if (flowNotificationOption) {
    if (flowNotificationOption === CustomerNotificationsOptions.RequiredEmail) {
      isEmailValid = !!email && EMAIL_REGEXP.test(email);
    } else if (
      flowNotificationOption === CustomerNotificationsOptions.TextOrEmail
    ) {
      isEmailValid = isEmailChecked ? EMAIL_REGEXP.test(email) : true;
    } else if (
      flowNotificationOption === CustomerNotificationsOptions.OptionalEmail
    ) {
      isEmailValid = email === '' || EMAIL_REGEXP.test(email);
    }
  } else {
    //removeable when theme.settings is deprecated
    // eslint-disable-next-line no-lonely-if
    if (requiredCustomerEmailFieldEnabled) {
      isEmailValid = !!email && EMAIL_REGEXP.test(email);
    } else if (customerNotificationPreferencesEnabled) {
      isEmailValid = isEmailChecked ? EMAIL_REGEXP.test(email) : true;
    } else if (optionalCustomerEmailFieldEnabled) {
      isEmailValid = email === '' || EMAIL_REGEXP.test(email);
    }
  }

  const isUnavailable = selectedTimeSlot?.isUnavailable ?? false;
  const isNotificationPreferenceValid =
    flowNotificationOption === CustomerNotificationsOptions.TextOrEmail ||
    customerNotificationPreferencesEnabled
      ? notificationPreference.length > 0
      : true;
  const isRequiredEmailValid = !!email && EMAIL_REGEXP.test(email);
  const isFormValid =
    mobileNotificationOptIn || erpKey !== 'servicetitan'
      ? isEmailValid &&
        isNotificationPreferenceValid &&
        (isDispatchFeeAccepted || isUnavailable)
      : isRequiredEmailValid && (isDispatchFeeAccepted || isUnavailable);
  const isContinueButtonDisabled =
    !isFormValid ||
    isUnavailable ||
    isLoading ||
    currentItemId !== CarouselItemId.RepairSummary;
  const errorMessage =
    error && !isLoading ? Phrase({ id: 'GENERIC_GRAPHQL_ERROR' }) : '';
  const continueButtonLabel = isUnavailable
    ? Phrase({ id: 'REPAIR_SUMMARY.BACK.LABEL' })
    : Phrase({ id: 'REPAIR_SUMMARY.CONTINUE.LABEL' });
  const continueButtonType = isUnavailable
    ? CarouselBottomButtonType.BACK
    : CarouselBottomButtonType.CONTINUE;

  const showMobileOrNonServiceTitan =
    mobileNotificationOptIn || erpKey !== 'servicetitan';

  const showNotificationPreferenceSection =
    flowNotificationOption === CustomerNotificationsOptions.TextOrEmail ||
    customerNotificationPreferencesEnabled !== false;

  const showOptionalEmailSection =
    (flowNotificationOption &&
      (flowNotificationOption === CustomerNotificationsOptions.OptionalEmail ||
        flowNotificationOption ===
          CustomerNotificationsOptions.RequiredEmail)) ||
    (customerNotificationPreferencesEnabled === false &&
      (optionalCustomerEmailFieldEnabled || requiredCustomerEmailFieldEnabled));

  return (
    <Form>
      <Container>
        {showMobileOrNonServiceTitan ? (
          <>
            {showNotificationPreferenceSection && (
              <NotificationPreferenceContainer
                isEmailChecked={isEmailChecked}
                isUnavailable={isUnavailable}
              >
                <PillContainer>
                  <PillGroup
                    onChange={handleNotificationPreferenceChange}
                    options={options}
                    title={Phrase({ id: 'REPAIR_SUMMARY.LEGEND' })}
                    value={[...notificationPreference]}
                  />
                </PillContainer>
                <EmailContainer
                  customerNotificationPreferencesEnabled
                  isEmailChecked={isEmailChecked}
                >
                  <TextBox
                    autoComplete="email"
                    isSuccessful={isEmailValid && isEmailChecked}
                    label={Phrase({ id: 'REPAIR_SUMMARY.EMAIL.LABEL' })}
                    leftIcon={InputEmailSvg}
                    name="email"
                    onChange={handleEmailChange}
                    type="email"
                    value={email}
                  />
                </EmailContainer>
              </NotificationPreferenceContainer>
            )}
            {showOptionalEmailSection && (
              <EmailContainer
                customerNotificationPreferencesEnabled={false}
                isEmailChecked={true}
              >
                <EmailTitle>
                  {Phrase({ id: 'REPAIR_SUMMARY.EMAIL.TITLE' })}
                </EmailTitle>
                <TextBox
                  autoComplete="email"
                  isSuccessful={isEmailValid && email !== ''}
                  label={Phrase({ id: 'REPAIR_SUMMARY.EMAIL.LABEL' })}
                  leftIcon={InputEmailSvg}
                  name="email"
                  onChange={handleEmailChange}
                  type="email"
                  value={email}
                />
              </EmailContainer>
            )}
          </>
        ) : (
          <EmailContainer
            customerNotificationPreferencesEnabled={false}
            isEmailChecked={true}
          >
            <EmailTitle>
              {erpKey === 'servicetitan' && !mobileNotificationOptIn
                ? 'Please provide your email address'
                : Phrase({ id: 'REPAIR_SUMMARY.EMAIL.TITLE' })}
            </EmailTitle>
            <TextBox
              autoComplete="email"
              isSuccessful={isEmailValid && email !== ''}
              label={Phrase({ id: 'REPAIR_SUMMARY.EMAIL.LABEL' })}
              leftIcon={InputEmailSvg}
              name="email"
              onChange={handleEmailChange}
              type="email"
              value={email}
            />
          </EmailContainer>
        )}
        <CheckBoxContainer>
          {dispatchFeeTooltipEnabled ? (
            <>
              <CheckBox
                checked={isDispatchFeeAccepted}
                onChange={handleCheckboxChange}
              >
                <Phrase id="REPAIR_SUMMARY.DISPATCH_FEE.LABEL" />
              </CheckBox>
              <InfoTooltip>
                <Phrase id="REPAIR_SUMMARY.DISPATCH_FEE.TOOLTIP" />
              </InfoTooltip>
            </>
          ) : (
            <>
              <DispatchFeeDescription>
                <Phrase id="REPAIR_SUMMARY.DISPATCH_FEE.TOOLTIP" />
              </DispatchFeeDescription>
              <CheckBox
                checked={isDispatchFeeAccepted}
                onChange={handleCheckboxChange}
              >
                <Phrase id="REPAIR_SUMMARY.DISPATCH_FEE.LABEL" />
              </CheckBox>
            </>
          )}
        </CheckBoxContainer>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          buttonType={continueButtonType}
          carouselItemId={CAROUSEL_ITEM_ID}
          continueButtonLabel={continueButtonLabel}
          errorMessage={errorMessage}
          isDisabled={isContinueButtonDisabled}
          isLoading={isLoading}
          onContinueClick={handleContinueClick}
        />
      </Container>
    </Form>
  );
};
